import { dispatch } from 'd3-dispatch'
import { dragDisable, dragEnable } from 'd3-drag'
import { interpolate } from 'd3-interpolate'
import { customEvent, event, touch, mouse, select } from 'd3-selection'
import { interrupt } from 'd3-transition'
import constant from './constant.js'
import BrushEvent from './event.js'
import noevent, { nopropagation } from './noevent.js'

var MODE_DRAG = { name: 'drag' },
  MODE_SPACE = { name: 'space' },
  MODE_HANDLE = { name: 'handle' },
  MODE_CENTER = { name: 'center' }

function number1(e) {
  return [+e[0], +e[1]]
}

function number2(e) {
  return [number1(e[0]), number1(e[1])]
}

function toucher(identifier) {
  return function (target) {
    return touch(target, event.touches, identifier)
  }
}

var X = {
  name: 'x',
  handles: ['w', 'e'].map(type),
  input: function (x, e) {
    return x == null
      ? null
      : [
          [+x[0], e[0][1]],
          [+x[1], e[1][1]],
        ]
  },
  output: function (xy) {
    return xy && [xy[0][0], xy[1][0]]
  },
}

var Y = {
  name: 'y',
  handles: ['n', 's'].map(type),
  input: function (y, e) {
    return y == null
      ? null
      : [
          [e[0][0], +y[0]],
          [e[1][0], +y[1]],
        ]
  },
  output: function (xy) {
    return xy && [xy[0][1], xy[1][1]]
  },
}

var XY = {
  name: 'xy',
  handles: ['n', 'w', 'e', 's', 'nw', 'ne', 'sw', 'se'].map(type),
  input: function (xy) {
    return xy == null ? null : number2(xy)
  },
  output: function (xy) {
    return xy
  },
}

var cursors = {
  overlay: 'crosshair',
  selection: 'move',
  n: 'ns-resize',
  e: 'ew-resize',
  s: 'ns-resize',
  w: 'ew-resize',
  nw: 'nwse-resize',
  ne: 'nesw-resize',
  se: 'nwse-resize',
  sw: 'nesw-resize',
}

var flipX = {
  e: 'w',
  w: 'e',
  nw: 'ne',
  ne: 'nw',
  se: 'sw',
  sw: 'se',
}

var flipY = {
  n: 's',
  s: 'n',
  nw: 'sw',
  ne: 'se',
  se: 'ne',
  sw: 'nw',
}

var signsX = {
  overlay: +1,
  selection: +1,
  n: null,
  e: +1,
  s: null,
  w: -1,
  nw: -1,
  ne: +1,
  se: +1,
  sw: -1,
}

var signsY = {
  overlay: +1,
  selection: +1,
  n: -1,
  e: null,
  s: +1,
  w: null,
  nw: -1,
  ne: -1,
  se: +1,
  sw: +1,
}

function type(t) {
  return { type: t }
}

// Ignore right-click, since that should open the context menu.
function defaultFilter() {
  return !event.ctrlKey && !event.button
}

function defaultExtent() {
  var svg = this.ownerSVGElement || this
  if (svg.hasAttribute('viewBox')) {
    svg = svg.viewBox.baseVal
    return [
      [svg.x, svg.y],
      [svg.x + svg.width, svg.y + svg.height],
    ]
  }
  return [
    [0, 0],
    [svg.width.baseVal.value, svg.height.baseVal.value],
  ]
}

function defaultTouchable() {
  return navigator.maxTouchPoints || 'ontouchstart' in this
}

// Like d3.local, but with the name “__brush” rather than auto-generated.
function local(node) {
  while (!node.__brush) if (!(node = node.parentNode)) return
  return node.__brush
}

function empty(extent) {
  return extent[0][0] === extent[1][0] || extent[0][1] === extent[1][1]
}

export function brushSelection(node) {
  var state = node.__brush
  return state ? state.dim.output(state.selection) : null
}

export function brushX() {
  return brush(X)
}

export function brushY() {
  return brush(Y)
}

export default function () {
  return brush(XY)
}

function brush(dim) {
  var extent = defaultExtent,
    filter = defaultFilter,
    touchable = defaultTouchable,
    keys = true,
    listeners = dispatch('start', 'brush', 'end'),
    handleSize = 6,
    touchending

  function brush(group) {
    var overlay = group
      .property('__brush', initialize)
      .selectAll('.overlay')
      .data([type('overlay')])

    overlay
      .enter()
      .append('rect')
      .attr('class', 'overlay')
      .attr('pointer-events', 'all')
      .attr('cursor', cursors.overlay)
      .merge(overlay)
      .each(function () {
        var extent = local(this).extent
        select(this)
          .attr('x', extent[0][0])
          .attr('y', extent[0][1])
          .attr('width', extent[1][0] - extent[0][0])
          .attr('height', extent[1][1] - extent[0][1])
      })

    group
      .selectAll('.selection')
      .data([type('selection')])
      .enter()
      .append('rect')
      .attr('class', 'selection')
      .attr('cursor', cursors.selection)
      .attr('fill', '#777')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#fff')
      .attr('shape-rendering', 'crispEdges')

    var handle = group.selectAll('.handle').data(dim.handles, function (d) {
      return d.type
    })

    handle.exit().remove()

    handle
      .enter()
      .append('rect')
      .attr('class', function (d) {
        return 'handle handle--' + d.type
      })
      .attr('cursor', function (d) {
        return cursors[d.type]
      })

    group
      .each(redraw)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousedown.brush', started)
      .filter(touchable)
      .on('touchstart.brush', started)
      .on('touchmove.brush', touchmoved)
      .on('touchend.brush touchcancel.brush', touchended)
      .style('touch-action', 'none')
      .style('-webkit-tap-highlight-color', 'rgba(0,0,0,0)')
  }

  brush.move = function (group, selection) {
    if (group.selection) {
      group
        .on('start.brush', function () {
          emitter(this, arguments).beforestart().start()
        })
        .on('interrupt.brush end.brush', function () {
          emitter(this, arguments).end()
        })
        .tween('brush', function () {
          var that = this,
            state = that.__brush,
            emit = emitter(that, arguments),
            selection0 = state.selection,
            selection1 = dim.input(
              typeof selection === 'function' ? selection.apply(this, arguments) : selection,
              state.extent
            ),
            i = interpolate(selection0, selection1)

          function tween(t) {
            state.selection = t === 1 && selection1 === null ? null : i(t)
            redraw.call(that)
            emit.brush()
          }

          return selection0 !== null && selection1 !== null ? tween : tween(1)
        })
    } else {
      group.each(function () {
        var that = this,
          args = arguments,
          state = that.__brush,
          selection1 = dim.input(
            typeof selection === 'function' ? selection.apply(that, args) : selection,
            state.extent
          ),
          emit = emitter(that, args).beforestart()

        interrupt(that)
        state.selection = selection1 === null ? null : selection1
        redraw.call(that)
        emit.start().brush().end()
      })
    }
  }

  brush.clear = function (group) {
    brush.move(group, null)
  }

  function redraw() {
    var group = select(this),
      selection = local(this).selection

    if (selection) {
      group
        .selectAll('.selection')
        .style('display', null)
        .attr('x', selection[0][0])
        .attr('y', selection[0][1])
        .attr('width', selection[1][0] - selection[0][0])
        .attr('height', selection[1][1] - selection[0][1])

      group
        .selectAll('.handle')
        .style('display', null)
        .attr('x', function (d) {
          return d.type[d.type.length - 1] === 'e'
            ? selection[1][0] - handleSize / 2
            : selection[0][0] - handleSize / 2
        })
        .attr('y', function (d) {
          return d.type[0] === 's'
            ? selection[1][1] - handleSize / 2
            : selection[0][1] - handleSize / 2
        })
        .attr('width', function (d) {
          return d.type === 'n' || d.type === 's'
            ? selection[1][0] - selection[0][0] + handleSize
            : handleSize
        })
        .attr('height', function (d) {
          return d.type === 'e' || d.type === 'w'
            ? selection[1][1] - selection[0][1] + handleSize
            : handleSize
        })
    } else {
      group
        .selectAll('.selection,.handle')
        .style('display', 'none')
        .attr('x', null)
        .attr('y', null)
        .attr('width', null)
        .attr('height', null)
    }
  }

  function emitter(that, args, clean) {
    return (!clean && that.__brush.emitter) || new Emitter(that, args)
  }

  function Emitter(that, args) {
    this.that = that
    this.args = args
    this.state = that.__brush
    this.active = 0
  }

  Emitter.prototype = {
    beforestart: function () {
      if (++this.active === 1) (this.state.emitter = this), (this.starting = true)
      return this
    },
    start: function () {
      if (this.starting) (this.starting = false), this.emit('start')
      else this.emit('brush')
      return this
    },
    brush: function () {
      this.emit('brush')
      return this
    },
    end: function () {
      if (--this.active === 0) delete this.state.emitter, this.emit('end')
      return this
    },
    emit: function (type) {
      customEvent(
        new BrushEvent(brush, type, dim.output(this.state.selection)),
        listeners.apply,
        listeners,
        [type, this.that, this.args]
      )
    },
  }
  function started() {
    if (event.touches) {
      if (event.changedTouches.length < event.touches.length) return noevent()
    } else if (touchending) return
    if (!filter.apply(this, arguments)) return

    var that = this,
      // eslint-disable-next-line
      type = event.target.__data__.type,
      mode =
        (event.metaKey ? (type = 'overlay') : type) === 'selection'
          ? MODE_DRAG
          : event.altKey
          ? MODE_CENTER
          : MODE_HANDLE,
      signX = dim === Y ? null : signsX[type],
      signY = dim === X ? null : signsY[type],
      state = local(that),
      // eslint-disable-next-line
      extent = state.extent,
      selection = state.selection,
      W = extent[0][0],
      w0,
      w1,
      N = extent[0][1],
      n0,
      n1,
      E = extent[1][0],
      e0,
      e1,
      S = extent[1][1],
      s0,
      s1,
      dx,
      dy,
      moving,
      shifting = signX && signY && event.shiftKey,
      lockX,
      lockY,
      point0 = mouse(that),
      point = point0,
      emit = emitter(that, arguments).beforestart()

    if (type === 'overlay') {
      state.selection = selection = [
        [(w0 = dim === Y ? W : point0[0]), (n0 = dim === X ? N : point0[1])],
        [(e0 = dim === Y ? E : w0), (s0 = dim === X ? S : n0)],
      ]
    } else {
      w0 = selection[0][0]
      n0 = selection[0][1]
      e0 = selection[1][0]
      s0 = selection[1][1]
    }

    w1 = w0
    n1 = n0
    e1 = e0
    s1 = s0

    const group = select(that).attr('pointer-events', 'none')

    const overlay = group.selectAll('.overlay').attr('cursor', cursors[type])

    if (event.touches) {
      group.on('touchmove.brush', moved, true).on('touchend.brush touchcancel.brush', ended, true)
    } else {
      var view = select(event.view)
        .on('keydown.brush', keydowned, true)
        .on('keyup.brush', keyupped, true)
        .on('mousemove.brush', moved, true)
        .on('mouseup.brush', ended, true)

      dragDisable(event.view)
    }

    nopropagation()
    interrupt(that)
    redraw.call(that)
    emit.start()

    function moved() {
      const point1 = mouse(that)
      // if (shifting && !lockX && !lockY) {
      //   if (Math.abs(point1[0] - point[0]) > Math.abs(point1[1] - point[1])) lockY = true;
      //   else lockX = true;
      // }
      point = point1
      moving = true
      noevent()
      move()
    }

    function move() {
      var t

      dx = point[0] - point0[0]
      dy = point[1] - point0[1]

      switch (mode) {
        case MODE_SPACE:
        case MODE_DRAG: {
          if (signX) (dx = Math.max(W - w0, Math.min(E - e0, dx))), (w1 = w0 + dx), (e1 = e0 + dx)
          if (signY) (dy = Math.max(N - n0, Math.min(S - s0, dy))), (n1 = n0 + dy), (s1 = s0 + dy)
          break
        }
        case MODE_HANDLE: {
          if (signX < 0) (dx = Math.max(W - w0, Math.min(E - w0, dx))), (w1 = w0 + dx), (e1 = e0)
          else if (signX > 0)
            (dx = Math.max(W - e0, Math.min(E - e0, dx))), (w1 = w0), (e1 = e0 + dx)
          if (signY < 0) (dy = Math.max(N - n0, Math.min(S - n0, dy))), (n1 = n0 + dy), (s1 = s0)
          else if (signY > 0)
            (dy = Math.max(N - s0, Math.min(S - s0, dy))), (n1 = n0), (s1 = s0 + dy)
          break
        }
        case MODE_CENTER: {
          if (signX)
            (w1 = Math.max(W, Math.min(E, w0 - dx * signX))),
              (e1 = Math.max(W, Math.min(E, e0 + dx * signX)))
          if (signY)
            (n1 = Math.max(N, Math.min(S, n0 - dy * signY))),
              (s1 = Math.max(N, Math.min(S, s0 + dy * signY)))
          break
        }
      }

      if (e1 < w1) {
        signX *= -1
        ;(t = w0), (w0 = e0), (e0 = t)
        ;(t = w1), (w1 = e1), (e1 = t)
        if (type in flipX) overlay.attr('cursor', cursors[(type = flipX[type])])
      }

      if (s1 < n1) {
        signY *= -1
        ;(t = n0), (n0 = s0), (s0 = t)
        ;(t = n1), (n1 = s1), (s1 = t)
        if (type in flipY) overlay.attr('cursor', cursors[(type = flipY[type])])
      }

      if (state.selection) selection = state.selection // May be set by brush.move!
      if (lockX) (w1 = selection[0][0]), (e1 = selection[1][0])
      if (lockY) (n1 = selection[0][1]), (s1 = selection[1][1])

      if (
        selection[0][0] !== w1 ||
        selection[0][1] !== n1 ||
        selection[1][0] !== e1 ||
        selection[1][1] !== s1
      ) {
        state.selection = [
          [w1, n1],
          [e1, s1],
        ]
        redraw.call(that)
        emit.brush()
      }
    }

    function ended() {
      nopropagation()
      if (event.touches) {
        if (event.touches.length) return
        if (touchending) clearTimeout(touchending)
        touchending = setTimeout(function () {
          touchending = null
        }, 500) // Ghost clicks are delayed!
        group.on('touchmove.brush touchend.brush touchcancel.brush', null)
      } else {
        dragEnable(event.view, moving)
        view.on('keydown.brush keyup.brush mousemove.brush mouseup.brush', null)
      }
      group.attr('pointer-events', 'all')
      overlay.attr('cursor', cursors.overlay)
      if (state.selection) selection = state.selection // May be set by brush.move (on start)!
      if (empty(selection)) (state.selection = null), redraw.call(that)
      emit.end()
    }

    function keydowned() {
      switch (event.keyCode) {
        case 16: {
          // SHIFT
          shifting = signX && signY
          break
        }
        case 18: {
          // ALT
          if (mode === MODE_HANDLE) {
            if (signX) (e0 = e1 - dx * signX), (w0 = w1 + dx * signX)
            if (signY) (s0 = s1 - dy * signY), (n0 = n1 + dy * signY)
            mode = MODE_CENTER
            move()
          }
          break
        }
        case 32: {
          // SPACE; takes priority over ALT
          if (mode === MODE_HANDLE || mode === MODE_CENTER) {
            if (signX < 0) e0 = e1 - dx
            else if (signX > 0) w0 = w1 - dx
            if (signY < 0) s0 = s1 - dy
            else if (signY > 0) n0 = n1 - dy
            mode = MODE_SPACE
            overlay.attr('cursor', cursors.selection)
            move()
          }
          break
        }
        default:
          return
      }
      noevent()
    }

    function keyupped() {
      switch (event.keyCode) {
        case 16: {
          // SHIFT
          if (shifting) {
            // lockX = (lockY = (shifting = false));
            move()
          }
          break
        }
        case 18: {
          // ALT
          if (mode === MODE_CENTER) {
            if (signX < 0) e0 = e1
            else if (signX > 0) w0 = w1
            if (signY < 0) s0 = s1
            else if (signY > 0) n0 = n1
            mode = MODE_HANDLE
            move()
          }
          break
        }
        case 32: {
          // SPACE
          if (mode === MODE_SPACE) {
            if (event.altKey) {
              if (signX) (e0 = e1 - dx * signX), (w0 = w1 + dx * signX)
              if (signY) (s0 = s1 - dy * signY), (n0 = n1 + dy * signY)
              mode = MODE_CENTER
            } else {
              if (signX < 0) e0 = e1
              else if (signX > 0) w0 = w1
              if (signY < 0) s0 = s1
              else if (signY > 0) n0 = n1
              mode = MODE_HANDLE
            }
            overlay.attr('cursor', cursors[type])
            move()
          }
          break
        }
        default:
          return
      }
      noevent()
    }
  }

  function touchmoved() {
    emitter(this, arguments).moved()
  }

  function touchended() {
    emitter(this, arguments).ended()
  }

  function initialize() {
    var state = this.__brush || { selection: null }
    state.extent = number2(extent.apply(this, arguments))
    state.dim = dim
    return state
  }

  brush.extent = function (_) {
    return arguments.length
      ? ((extent = typeof _ === 'function' ? _ : constant(number2(_))), brush)
      : extent
  }

  brush.filter = function (_) {
    return arguments.length
      ? ((filter = typeof _ === 'function' ? _ : constant(!!_)), brush)
      : filter
  }

  brush.handleSize = function (_) {
    return arguments.length ? ((handleSize = +_), brush) : handleSize
  }

  brush.keyModifiers = function (_) {
    return arguments.length ? ((keys = !!_), brush) : keys
  }

  brush.on = function () {
    var value = listeners.on.apply(listeners, arguments)
    return value === listeners ? brush : value
  }

  return brush
}
